/**
 * React-query hooks for public.test_account_filters — the dynamically-added
 * half of the Test / CoLiviq account predicate (see src/lib/is-test-account.ts
 * for the static half: domain + seed emails).
 *
 * RLS is admin-only for every operation, so this is only ever queried from
 * admin surfaces (Directory, Bookings & Tenants, Analytics, Owners).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type TestAccountFilterRow = {
  id: string;
  email: string;
  created_by: string | null;
  created_at: string;
};

const QK = {
  list: () => ["test-account-filters"] as const,
};

export function useTestAccountFilters(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: QK.list(),
    enabled,
    queryFn: async (): Promise<TestAccountFilterRow[]> => {
      const { data, error } = await supabase
        .from("test_account_filters")
        .select("id, email, created_by, created_at")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as TestAccountFilterRow[];
    },
    staleTime: 30_000,
  });
}

// Adding an email is handled by the admin-invite-user edge function (see
// AdminRolePanels.tsx's "Add test email" dialog) — that path also resolves
// or creates the account behind the email, atomically with registering it
// here, so there's no standalone "just insert a filter row" mutation.

export function useRemoveTestAccountFilter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase
        .from("test_account_filters")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.list() });
    },
  });
}
